import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { Band, PullQuote } from './Band';
import { scenarioClasses } from '../../data/site';
const FIELD_LABEL = 'font-mono text-xs uppercase tracking-[0.14em]';
export function Scenarios() {
  const [selected, setSelected] = useState(0);
  const [open, setOpen] = useState(false);
  const active = scenarioClasses[selected];
  const example = active.example;
  return <Band id="scenarios" eyebrow="How the assessment works" title="Four kinds of scenario. Only one contains a hidden AI error." description="Six scenarios per session. Only one contains a genuine error. The rest test whether candidates verify AI output, resolve conflicting evidence, connect information across documents, and recognise when the evidence is already correct." aside={<PullQuote data-id="element-314" data-mp="277o1y96b6owd">
          Lentic doesn't test whether a candidate can get the right answer. It
          tests how they behave when AI gives them one.
        </PullQuote>} data-id="element-313" data-mp="2pxce6l2drgme">

      <div className="grid gap-4 md:grid-cols-2" data-id="element-315" data-mp="11dvs102ac6xm">
        {scenarioClasses.map((item, i) => {
        const isActive = i === selected;
        return <button key={item.id} type="button" onClick={() => setSelected(i)} aria-pressed={isActive} className={`flex flex-col rounded-2xl border p-8 text-left transition-colors duration-200 ${isActive ? 'border-teal bg-white shadow-[0_20px_50px_-40px_rgba(14,36,54,0.6)]' : 'border-mist bg-white/60 hover:border-ink/25'}`} data-id="element-316" data-mp="1bc1nozxi0gu1" data-mpi={i}>
              <span className="flex items-baseline gap-3" data-id="element-317" data-mp="3yhrmzjb60c6">
                <span className={`${FIELD_LABEL} text-muted`} data-id="element-318" data-mp="3wwx39wzuheb">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className={`${FIELD_LABEL} tracking-[0.2em] ${isActive ? 'text-teal' : 'text-muted'}`} data-id="element-319" data-mp="1z261enqzrj6o">
                  {item.count}
                </span>
              </span>
              <h3 className="mt-4 font-display text-2xl font-medium text-ink" data-id="element-320" data-mp="28shht9rv3kug">
                {item.name}
              </h3>
              <p className="mt-4 max-w-[46ch] text-[17px] leading-relaxed text-graphite" data-id="element-321" data-mp="1g31ylwq1mlng">
                {item.what}
              </p>
              <p className="mt-auto max-w-[46ch] pt-6 text-[15px] leading-relaxed text-ink" data-id="element-322" data-mp="1l36f3mqybgdo">
                <span className="font-semibold text-teal" data-id="element-323" data-mp="1q0ygd7dhkeuj">Reveals. </span>
                {item.reveals}
              </p>
            </button>;
      })}
      </div>

      <figure className="mt-6 flex flex-col items-start" data-id="element-324" data-mp="31gn5o5e7ppyi">
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink/40" data-id="element-325" data-mp="31g3mklctsviw">
          {open ? 'Hide example' : 'See an example'}
          <ChevronDownIcon className={`h-4 w-4 text-teal transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-326" data-mp="l21mu7rjwyms" />
        </button>

        <Link to="/methodology" className="order-last mt-6 inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink/40" data-id="element-327" data-mp="b3vlbr9npuig">
          Methodology
          <ArrowRightIcon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-328" data-mp="23pbqgh2xeok" />
        </Link>

        <div hidden={!open} className="mt-5 w-full rounded-2xl border border-mist bg-white px-7 py-6" data-id="element-329" data-mp="3tbb95rd7gkwz">
          <figcaption className={`${FIELD_LABEL} tracking-[0.2em] text-muted`} data-id="element-330" data-mp="1wgyfrsquw7zf">
            Example
          </figcaption>

          <AnimatePresence mode="wait" initial={false} data-id="element-331" data-mp="1rgkfiwvhy2by">
            <motion.div key={active.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-332" data-mp="p6ae3qzfhrzo">
              <p className={`mt-4 ${FIELD_LABEL} tracking-[0.2em] text-teal`} data-id="element-333" data-mp="k5mg2lcs8avy">
                {example.label}
              </p>

              <p className="mt-4 max-w-[76ch] text-lg leading-relaxed text-ink" data-id="element-334" data-mp="5fx6q2ujd0kl">
                {example.body}
              </p>

              <div className="mt-6 border-t border-mist pt-5" data-id="element-335" data-mp="a9t3j1z2che6">
                <p className={`${FIELD_LABEL} text-graphite`} data-id="element-336" data-mp="3smqwgoxfu9yu">Candidate action</p>
                <p className="mt-2 max-w-[76ch] text-[17px] leading-relaxed text-ink" data-id="element-337" data-mp="1ev4dd5bo8ee">
                  {example.action}
                </p>
              </div>

              <div className="mt-5" data-id="element-338" data-mp="3ipumsfvszhsp">
                <p className={`${FIELD_LABEL} ${example.adverse ? 'text-clay' : 'text-teal'}`} data-id="element-339" data-mp="3nmusuqsnkkjt">
                  Recorded outcome ·{' '}
                  <span className="font-semibold" data-id="element-340" data-mp="34131653irvmv">{example.outcome}</span>
                </p>
                {example.outcomeNote ? <p className="mt-2 text-[15px] leading-relaxed text-graphite" data-id="element-341" data-mp="3dyz6uhdotydl">
                    {example.outcomeNote}
                  </p> : null}
                {example.closing ? <p className="mt-2 max-w-[76ch] text-[15px] leading-relaxed text-graphite" data-id="element-342" data-mp="3mzfc9wdzrfl3">
                    {example.closing}
                  </p> : null}
              </div>

              <p className="mt-6 max-w-[76ch] border-t border-mist pt-5 text-[15px] leading-relaxed text-ink" data-id="element-343" data-mp="jt6lqns7vy6">
                <span className={`${FIELD_LABEL} text-teal`} data-id="element-344" data-mp="3rpvrnm1dhba3">In the decision record · </span>
                {example.inRecord}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </figure>
    </Band>;
}